import { KoobButton, KoobModal, KoobInputText, KoobInputLabel } from '@koob/ui';
import {
  createToConnectionRequestModel,
  updateToConnectionRequestModel,
} from 'api/node/toConnectionRequestModel';
import Editor from 'components/Fields/Editor';
import formatToconnectionRequestModelData from 'containers/ToConnectionRequest/utils';
import { useSnack } from 'hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';

// React-Quill never hands back a truly empty string: an editor the user typed
// in and then cleared still reports '<p><br></p>'. The description is optional,
// so normalise those markers back to '' — otherwise an "empty" description is
// stored (and treated) as filled content.
const EMPTY_EDITOR_CONTENTS = ['<p><br></p>', '<p></p>'];

const normalizeDescription = (value) =>
  !value || EMPTY_EDITOR_CONTENTS.includes(value.trim()) ? '' : value;

const ToConnectionRequestModelModal = ({
  model,
  mode,
  connectionRequestData,
  open,
  setOpen,
}) => {
  const { t } = useTranslation('toConnectionRequest');
  const { notify } = useSnack();
  const form = useFormikContext();
  const [modelName, setModelName] = useState(
    mode === 'create' ? '' : model?.name || '',
  );
  const [modelDescription, setModelDescription] = useState(
    mode === 'create' ? '' : model?.description || '',
  );
  const isUpdateMode = (mode ?? (model?.id ? 'update' : 'create')) === 'update';

  const [editorMounted, setEditorMounted] = useState(false);
  useEffect(() => {
    setEditorMounted(true);
  }, []);

  const handleSave = async () => {
    if (!modelName.trim()) {
      return;
    }

    try {
      const products = formatToconnectionRequestModelData(
        connectionRequestData,
      );

      if (products.length === 0) {
        notify(t('errors:error', { name: 'products' }), { type: 'error' });
        return;
      }
      const description = normalizeDescription(modelDescription);

      const payload = {
        name: modelName.trim(),
        description,
        products,
      };

      if (isUpdateMode) {
        await updateToConnectionRequestModel({ id: model.id, payload });
        form.setFieldValue('model', {
          id: model.id,
          name: modelName.trim(),
          description,
        });
        notify(t('models.messages.updated'));
      } else {
        await createToConnectionRequestModel({ payload });
        notify(t('models.messages.created'));
      }
      setOpen(false);
    } catch (error) {
      if (error?.response?.data?.message) {
        notify(error?.response?.data?.message, { type: 'error' });
      } else {
        notify(t('errors:error'), { type: 'error' });
      }
    }
  };

  return (
    <KoobModal open={open} setOpen={setOpen}>
      <div>
        <h3 className="text-lg font-semibold text-gray-800">
          {isUpdateMode
            ? t('models.modal.editTitle')
            : t('models.modal.createTitle')}
        </h3>
        <p className="text-xs text-gray-400 mt-0.5">
          {isUpdateMode
            ? t('models.modal.editSubtitle')
            : t('models.modal.createSubtitle')}
        </p>
      </div>

      <div className="w-full py-6">
        <KoobInputLabel label={t('models.modal.label')} required>
          <KoobInputText
            placeholder={t('models.modal.placeholder')}
            value={modelName}
            type="text"
            onChange={(e) => {
              setModelName(e.target.value);
            }}
          />
        </KoobInputLabel>

        {/* Optional field: no `required`, so it never renders the red
            required marker nor an error state when left empty. */}
        <div className="w-full py-6">
          <KoobInputLabel label={t('models.modal.descriptionLabel')}>
            {editorMounted && (
              <Editor
                value={modelDescription}
                onChange={(value, _delta, source) => {
                  if (source === 'user') {
                    setModelDescription(value);
                  }
                }}
                name="description"
                placeholder={t('models.modal.descriptionPlaceholder')}
              />
            )}
          </KoobInputLabel>
        </div>
      </div>

      <div className="w-full flex items-center justify-between gap-6">
        <KoobButton
          color="secondary"
          type="button"
          onClick={() => setOpen(false)}
          block
        >
          {t('misc:cancel')}
        </KoobButton>

        <KoobButton
          variant="primary"
          type="button"
          disabled={!modelName.trim()}
          onClick={() => handleSave()}
          block
        >
          {t('misc:save')}
        </KoobButton>
      </div>
    </KoobModal>
  );
};

export default ToConnectionRequestModelModal;
